// Extracted from original/src/components/SuperCurrenciesSettings.tsx (781 lines; ADR-08 §2.1), the body of the Settings
// card "خانة السوبر (العملات)" (App.tsx:16737-16762, frame = `SettingsCardFrame` 'super_currencies'):
//   restricted panel      :68-75      warning panel :380-397     vault switch :399-439
//   add-currency form     :512-652    visibility / rates list :654-776
// Kept: class strings, icons and sizes, the form feedback banner, the per-row switches.
// Cut: every `localStorage` write and the balance arithmetic (:80-375) → callbacks (the API, own PIN, D-018, D-069);
// `currentUser.role` → `restricted` (the container: Super Manager only, D-069).
// Changed by decision (D-244): the "transfer IQD equivalent / clear balances" panel (:441-508) is removed (D-069 (1):
// switching the vault off hides the screens only); the add form asks for the buy **and** sell rate and the quote unit
// (D-021: separate rates, a quote unit per currency; V-18); each row edits buy and sell (D-021, the rate table D-126)
// in the quote unit the row names ("1,000,000 TOMAN ="); delete is refused by the server while any balance remains
// (D-069 (2)) and the message says so.
import { useState } from 'react';
import { Lock, Plus, Eye, EyeOff, ShieldAlert, Save, Trash2, Ban, Play, AlertTriangle, CheckCircle, Coins } from 'lucide-react';
import { useT } from '../i18n-context.js';

export interface CurrencySettingRow {
  readonly code: string;
  readonly name: string;
  readonly symbol: string;
  readonly multipleLabel: string;
  readonly quoteUnitLabel: string;
  readonly visible: boolean;
  readonly suspended: boolean;
  readonly buy: string;
  readonly sell: string;
}

export interface NewCurrencyValues {
  readonly code: string;
  readonly symbol: string;
  readonly nameAr: string;
  readonly nameEn: string;
  readonly buy: string;
  readonly sell: string;
  readonly quoteUnit: string;
  readonly multiple: string;
  readonly bills: string;
}

export interface CurrenciesCardProps {
  readonly restricted: boolean;
  readonly vaultVisible: boolean;
  readonly onToggleVault: () => void;
  readonly rows: readonly CurrencySettingRow[];
  readonly quoteUnits: readonly string[];
  /** Resolves to a feedback message (error or success) for the banner. */
  readonly onAdd: (v: NewCurrencyValues) => Promise<{ readonly type: 'error' | 'success'; readonly message: string } | null>;
  readonly onSaveRates: (code: string, buy: string, sell: string) => Promise<boolean>;
  readonly onToggleVisible: (code: string) => void;
  readonly onToggleSuspended: (code: string) => void;
  readonly onDelete: (code: string) => void;
  readonly busy: boolean;
}

const EMPTY: NewCurrencyValues = { code: '', symbol: '', nameAr: '', nameEn: '', buy: '', sell: '', quoteUnit: '1', multiple: '1', bills: '5,10,20,50,100,200,500' };

export function CurrenciesCard(props: CurrenciesCardProps) {
  const t = useT();
  const [v, setV] = useState<NewCurrencyValues>(EMPTY);
  const [formFeedback, setFormFeedback] = useState<{ type: 'error' | 'success'; message: string } | null>(null);
  const [editing, setEditing] = useState<Record<string, { buy: string; sell: string }>>({});
  const patch = (p: Partial<NewCurrencyValues>) => setV((x) => ({ ...x, ...p }));

  if (props.restricted) {
    return (
      <div className="p-8 text-center text-rose-500 font-bold bg-rose-50 dark:bg-rose-950/15 rounded-3xl border border-rose-100 dark:border-rose-900/30">
        <ShieldAlert size={48} className="mx-auto mb-4 opacity-75" />
        <p>{t('cur.restricted')}</p>
      </div>
    );
  }

  const add = async (e: React.FormEvent) => {
    e.preventDefault();
    const r = await props.onAdd(v);
    setFormFeedback(r);
    if (r?.type === 'success') setV(EMPTY);
  };
  const show = props.vaultVisible;

  return (
    <div className="space-y-8" id="super_currencies_settings_container" data-testid="currencies-card">
      {show && (
        <div className="p-6 bg-rose-500/10 dark:bg-rose-950/15 border border-rose-500/20 rounded-[2rem] flex items-start gap-4">
          <div className="p-3 bg-rose-500/20 text-rose-600 dark:text-rose-400 rounded-2xl">
            <Lock size={24} />
          </div>
          <div className="space-y-1">
            <h4 className="font-black text-rose-800 dark:text-rose-400">{t('cur.zone_title')}</h4>
            <p className="text-xs text-rose-600 dark:text-rose-400/80 leading-relaxed">{t('cur.zone_text')}</p>
          </div>
        </div>
      )}

      {/* 1. Global Currency Vault Active Toggle */}
      <div className="p-6 bg-white dark:bg-[#0d1117] rounded-[2.5rem] border border-gray-150 dark:border-[#30363d] shadow-sm flex flex-col md:flex-row md:items-center justify-between gap-6">
        <div className="flex items-center gap-4">
          <div className={`p-3.5 rounded-2xl ${show ? 'bg-amber-500/10 text-amber-600 dark:text-amber-400' : 'bg-slate-100 dark:bg-white/5 text-slate-400'}`}>
            <Coins size={24} />
          </div>
          <div>
            <h4 className="font-black text-gray-900 dark:text-white">{t('cur.vault_title')}</h4>
            {show && <p className="text-xs text-slate-400 dark:text-gray-400">{t('cur.vault_text')}</p>}
          </div>
        </div>

        <button
          data-testid="cur-vault-toggle"
          onClick={props.onToggleVault}
          disabled={props.busy}
          className={`px-6 py-3 rounded-2xl text-xs font-black transition-all flex items-center gap-2 border shadow-sm ${
            show
              ? 'bg-amber-500 hover:bg-amber-600 border-amber-600 text-white'
              : 'bg-slate-100 hover:bg-slate-200 dark:bg-[#161b22] dark:hover:bg-[#30363d] border-gray-200 dark:border-[#30363d] text-slate-600 dark:text-slate-300'
          }`}
        >
          {show ? (
            <>
              <Eye size={16} />
              {t('cur.vault_on')}
            </>
          ) : (
            <>
              <EyeOff size={16} />
              {t('cur.vault_off')}
            </>
          )}
        </button>
      </div>

      {show && (
        <div className="grid grid-cols-1 gap-6 animate-fade-in">
          {/* Form to Add New Currency */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-[2.5rem] border border-gray-150 dark:border-[#30363d] shadow-sm space-y-6">
            <div className="flex items-center gap-3">
              <div className="p-2.5 bg-indigo-500/10 rounded-xl text-indigo-600 dark:text-indigo-400">
                <Plus size={20} />
              </div>
              <div>
                <h4 className="font-black text-gray-900 dark:text-white">{t('cur.add_title')}</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('cur.add_sub')}</p>
              </div>
            </div>

            <form onSubmit={(e) => void add(e)} className="space-y-4 text-xs font-bold" data-testid="cur-add-form">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.code')}</label>
                  <input type="text" required placeholder="E.g., CAD" value={v.code} onChange={(e) => patch({ code: e.target.value })} data-testid="cur-code" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold text-sm uppercase" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.symbol')}</label>
                  <input type="text" required placeholder="E.g., C$" value={v.symbol} onChange={(e) => patch({ symbol: e.target.value })} data-testid="cur-symbol" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-bold text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.name_ar')}</label>
                  <input type="text" required placeholder="دولار كندي" value={v.nameAr} onChange={(e) => patch({ nameAr: e.target.value })} data-testid="cur-name-ar" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-right font-bold" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.name_en')}</label>
                  <input type="text" required placeholder="Canadian Dollar" value={v.nameEn} onChange={(e) => patch({ nameEn: e.target.value })} data-testid="cur-name-en" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-left font-bold" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.buy')}</label>
                  <input type="text" required placeholder="E.g., 1050" value={v.buy} onChange={(e) => patch({ buy: e.target.value })} data-testid="cur-buy" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold text-sm" />
                </div>
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.sell')}</label>
                  <input type="text" required placeholder="E.g., 1080" value={v.sell} onChange={(e) => patch({ sell: e.target.value })} data-testid="cur-sell" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold text-sm" />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.quote_unit')}</label>
                  <select value={v.quoteUnit} onChange={(e) => patch({ quoteUnit: e.target.value })} data-testid="cur-quote-unit" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold text-sm">
                    {props.quoteUnits.map((u) => (
                      <option key={u} value={u}>
                        {u}
                      </option>
                    ))}
                  </select>
                </div>
                <div className="space-y-1">
                  <label className="text-slate-400">{t('cur.multiple')}</label>
                  <input type="text" required placeholder="1 or 500" value={v.multiple} onChange={(e) => patch({ multiple: e.target.value })} data-testid="cur-multiple" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold text-sm" />
                </div>
              </div>

              <div className="space-y-1">
                <label className="text-slate-400">{t('cur.bills')}</label>
                <input type="text" required placeholder="5,10,20,50,100" value={v.bills} onChange={(e) => patch({ bills: e.target.value })} data-testid="cur-bills" className="w-full bg-slate-50 dark:bg-black border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-2.5 text-center font-mono font-bold" />
              </div>

              {formFeedback && (
                <div
                  className={`p-3.5 rounded-2xl flex items-center gap-3 text-xs font-bold animate-fade-in ${
                    formFeedback.type === 'error' ? 'bg-rose-500/10 text-rose-600 dark:text-rose-400 border border-rose-500/20' : 'bg-emerald-500/10 text-emerald-600 dark:text-emerald-400 border border-emerald-500/20'
                  }`}
                >
                  {formFeedback.type === 'error' ? <AlertTriangle size={18} className="shrink-0 text-rose-500" /> : <CheckCircle size={18} className="shrink-0 text-emerald-500" />}
                  <span>{formFeedback.message}</span>
                </div>
              )}

              <button
                type="submit"
                disabled={props.busy}
                data-testid="cur-add"
                className="w-full bg-rose-600 hover:bg-rose-700 active:scale-[0.99] text-white font-black py-3.5 rounded-2xl transition-all shadow-md flex items-center justify-center gap-2 uppercase tracking-widest text-xs"
              >
                <Plus size={16} />
                {t('cur.add_button')}
              </button>
            </form>
          </div>

          {/* Visibility and Exchange Rates Panel */}
          <div className="bg-white dark:bg-[#0d1117] p-6 rounded-[2.5rem] border border-gray-150 dark:border-[#30363d] shadow-sm flex flex-col">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-2.5 bg-rose-500/10 rounded-xl text-rose-600 dark:text-rose-400">
                <ShieldAlert size={20} />
              </div>
              <div>
                <h4 className="font-black text-gray-900 dark:text-white">{t('cur.list_title')}</h4>
                <p className="text-[10px] font-bold text-gray-400 uppercase tracking-widest">{t('cur.list_sub')}</p>
              </div>
            </div>

            <div className="space-y-4 pr-1">
              {props.rows.map((c) => {
                const ed = editing[c.code] ?? { buy: c.buy, sell: c.sell };
                const hasChanged = ed.buy !== c.buy || ed.sell !== c.sell;
                return (
                  <div
                    key={c.code}
                    data-testid={`cur-row-${c.code}`}
                    className={`p-4 rounded-2xl border flex flex-col gap-4 text-xs font-bold transition-all ${
                      c.suspended ? 'bg-rose-500/5 border-rose-200/50 dark:border-rose-950/40' : 'bg-slate-50 dark:bg-white/5 border-gray-150 dark:border-[#30363d]'
                    }`}
                  >
                    <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                      <div className="flex items-center gap-3">
                        <button
                          onClick={() => props.onToggleVisible(c.code)}
                          disabled={props.busy}
                          data-testid={`cur-visible-${c.code}`}
                          className={`p-2 rounded-xl transition-all ${c.visible ? 'bg-emerald-500/15 text-emerald-500 hover:bg-emerald-500/25' : 'bg-rose-500/15 text-rose-500 hover:bg-rose-500/25'}`}
                          title={c.visible ? t('cur.hide') : t('cur.show')}
                        >
                          {c.visible ? <Eye size={18} /> : <EyeOff size={18} />}
                        </button>
                        <div>
                          <div className="flex items-center gap-2">
                            <span className="font-black text-gray-800 dark:text-white block">
                              {c.name} ({c.code})
                            </span>
                            {c.suspended && <span className="px-2 py-0.5 bg-rose-100 dark:bg-rose-950 text-rose-600 dark:text-rose-400 text-[9px] rounded-full font-black animate-pulse">{t('cur.suspended')}</span>}
                          </div>
                          <span className="text-[10px] text-slate-400 font-mono">
                            {t('cur.multiple_row')} {c.multipleLabel} {c.symbol}
                          </span>
                        </div>
                      </div>

                      <div className="flex items-center gap-2">
                        <div className="flex items-center bg-white dark:bg-[#0d1117] border border-gray-200 dark:border-[#30363d] rounded-xl px-3 py-1 text-xs">
                          <span className="font-mono text-slate-400 mr-2">
                            {c.quoteUnitLabel} {c.code} =
                          </span>
                          <input
                            type="text"
                            data-testid={`cur-buy-${c.code}`}
                            value={ed.buy}
                            onChange={(e) => setEditing({ ...editing, [c.code]: { ...ed, buy: e.target.value } })}
                            className="w-16 text-center bg-transparent border-none focus:ring-0 p-0 font-mono font-bold text-slate-800 dark:text-slate-200"
                            title={t('cur.buy')}
                          />
                          <span className="font-mono text-slate-400">/</span>
                          <input
                            type="text"
                            data-testid={`cur-sell-${c.code}`}
                            value={ed.sell}
                            onChange={(e) => setEditing({ ...editing, [c.code]: { ...ed, sell: e.target.value } })}
                            className="w-16 text-center bg-transparent border-none focus:ring-0 p-0 font-mono font-bold text-slate-800 dark:text-slate-200"
                            title={t('cur.sell')}
                          />
                          <span className="font-mono text-slate-400 ml-1">د.ع</span>
                        </div>
                        <button
                          disabled={!hasChanged || props.busy}
                          data-testid={`cur-save-${c.code}`}
                          onClick={async () => {
                            if (await props.onSaveRates(c.code, ed.buy, ed.sell)) {
                              const { [c.code]: _done, ...rest } = editing;
                              setEditing(rest);
                            }
                          }}
                          className="p-2 bg-rose-600 disabled:opacity-40 hover:bg-rose-700 text-white rounded-xl transition-all shadow-sm"
                          title={t('cur.save_rate')}
                        >
                          <Save size={14} />
                        </button>
                      </div>
                    </div>

                    <div className="flex items-center justify-end gap-2 pt-2 border-t border-gray-150/50 dark:border-[#30363d]/50">
                      <button
                        onClick={() => props.onToggleSuspended(c.code)}
                        disabled={props.busy}
                        data-testid={`cur-suspend-${c.code}`}
                        className={`px-3 py-1.5 rounded-xl text-[10px] font-black transition-all flex items-center gap-1.5 border shadow-sm ${
                          c.suspended
                            ? 'bg-emerald-500/10 border-emerald-500/20 text-emerald-600 dark:text-emerald-400 hover:bg-emerald-500/20'
                            : 'bg-amber-500/10 border-amber-500/20 text-amber-600 dark:text-amber-400 hover:bg-amber-500/20'
                        }`}
                      >
                        {c.suspended ? (
                          <>
                            <Play size={12} />
                            {t('cur.activate')}
                          </>
                        ) : (
                          <>
                            <Ban size={12} />
                            {t('cur.suspend')}
                          </>
                        )}
                      </button>

                      <button
                        onClick={() => props.onDelete(c.code)}
                        disabled={props.busy}
                        data-testid={`cur-delete-${c.code}`}
                        className="px-3 py-1.5 rounded-xl text-[10px] font-black bg-rose-500/10 hover:bg-rose-500/20 border border-rose-500/20 text-rose-600 dark:text-rose-400 transition-all flex items-center gap-1.5 shadow-sm"
                        title={t('cur.delete_title')}
                      >
                        <Trash2 size={12} />
                        {t('cur.delete')}
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
